import { createClient, type SupabaseClient } from '@supabase/supabase-js';

/**
 * Supabase Auth client (E2.2) — single source of truth for authentication
 *. The browser performs the Kakao OAuth handshake here and
 * the SDK owns token storage + refresh; the API only verifies the JWT.
 *
 * Env (build-time, public scope only —):
 *   VITE_SUPABASE_URL              project URL
 *   VITE_SUPABASE_PUBLISHABLE_KEY  publishable (public) key — new Supabase key system
 *
 * The legacy VITE_SUPABASE_ANON_KEY is still accepted as a fallback for backward
 * compatibility, but the publishable key (sb_publishable_…) is preferred.
 */
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey =
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? import.meta.env.VITE_SUPABASE_ANON_KEY;

let client: SupabaseClient | undefined;

/**
 * 클라이언트는 처음 쓸 때 만든다. 서버(SSR)가 이 모듈을 import 해도 죽지 않게 —
 * 모듈 초기화 때 env 를 검사하거나 `window` 를 읽지 않는다. 서버는 부르지 않는다.
 */
export function getSupabase(): SupabaseClient {
  if (client) return client;
  if (!supabaseUrl || !supabaseKey) {
    throw new Error(
      'Missing Supabase env: set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY.',
    );
  }
  client = createClient(supabaseUrl, supabaseKey, {
    auth: {
      // PKCE is the secure flow for browser SPAs ( step 2).
      flowType: 'pkce',
      // SDK persists + auto-refreshes the session so the JWT is ready for API calls.
      persistSession: true,
      autoRefreshToken: true,
      // Let the callback route trigger the code→token exchange explicitly.
      detectSessionInUrl: false,
    },
  });
  return client;
}

/**
 * Start the Kakao login (AC-1.2: "카카오로 시작하기"). Redirects the browser to
 * Kakao consent; control returns to /auth/callback with an authorization code.
 */
export async function signInWithKakao(): Promise<void> {
  // Where Kakao/Supabase redirects back to after consent (the callback route).
  const redirectTo = `${window.location.origin}/auth/callback`;
  const { error } = await getSupabase().auth.signInWithOAuth({
    provider: 'kakao',
    options: { redirectTo },
  });
  if (error) throw error;
}

/** Sign the user out and clear the stored session. */
export async function signOut(): Promise<void> {
  const { error } = await getSupabase().auth.signOut();
  if (error) throw error;
}
